import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
// components
import { IconButton } from 'components/UI'
// hooks
import { useOfflineRiskCreate } from 'hooks/offline'
import { useCurrentUser, useFormsLinking, useShowModal } from 'hooks/index'
// constants
import { Modals } from 'constants/index'
// icons
import { VerginaSunIcon } from 'components/UI/Icons'
// types
import { IRiskView } from 'types/graphql'
import { AiChatContext } from 'components/Modals/AiChatModal/types'
// context
import { RiskViewAPIContext } from 'containers/Risks/RiskView/layers/API'
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// icons
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
// views
import { AddIcon, ButtonsWrapper, FilterIcon, ScheduleReviewIcon, ViewLabel } from './views'
// utils
import { isEmpty } from 'lodash'

interface Props {
  riskView: IRiskView
}

const RiskViewButtons: React.FC<Props> = ({ riskView }) => {
  const router = useRouter()

  const [isCreating, setIsCreating] = React.useState(false)

  const { isFormView, isLinking } = useFormsLinking()

  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)
  const { isScheduled, showScheduledRisks } = React.useContext(RiskRegisterContext)
  const { createRisk, deleteRiskView, appliedFilters } = React.useContext(RiskViewAPIContext)

  const {
    permissionsFromSecurityGroups: { aiEnableUsage }
  } = useCurrentUser()

  const createRiskOffline = useOfflineRiskCreate()

  const isFilterActive = !isEmpty(appliedFilters)

  const { showModal } = useShowModal({ modalType: Modals.RISK_VIEW_CREATE_MODAL })
  const { showModal: showFilterDrawer } = useShowModal({ modalType: Modals.RISK_VIEW_FILTER_MODAL })
  const { showModal: showAiChat } = useShowModal({ modalType: Modals.AI_CHAT_MODAL })

  const {
    permissionsFromSecurityGroups: { manageRiskTemplates, scheduleRisks }
  } = useCurrentUser()

  const canCreateRisk =
    !navigator.onLine || (!isCreating && !riskView.riskTemplate.archived && riskTemplate.permissions?.riskCreate)

  const showRiskViewDrawer = () => showModal({ riskView })

  const showFilter = () => showFilterDrawer()

  const handleShowScheduled = () => {
    showScheduledRisks()
  }

  const onCreateRisk = () => {
    setIsCreating(true)

    if (!navigator.onLine) {
      const tempId = createRiskOffline(riskView.riskTemplate.id)

      router.push(`/forms/edit/${tempId}/template/${riskView.riskTemplate.id}`)
      return
    }

    createRisk().finally(() => setIsCreating(false))
  }

  const aiChatContext: AiChatContext = {
    type: 'riskView',
    title: riskView.name,
    riskViewId: riskView.id,
    riskTemplateId: riskView.riskTemplate.id
  }

  if (isLinking) {
    return null
  }

  return (
    <ButtonsWrapper>
      <ViewLabel>{isScheduled ? `${riskView.name} Schedules` : riskView.name}</ViewLabel>

      {!isFormView && aiEnableUsage && riskTemplate.aiEnabled && (
        <IconButton
          preset='PURPLE'
          onClick={() => showAiChat({ context: aiChatContext })}
          disabled={!canCreateRisk}
          description='Show AI Chat'>
          <VerginaSunIcon />
        </IconButton>
      )}
      {!isFormView && scheduleRisks && (
        <IconButton
          preset='PURPLE'
          isActive={isScheduled}
          onClick={handleShowScheduled}
          description='Show Scheduled Forms'>
          <ScheduleReviewIcon />
        </IconButton>
      )}
      {!isFormView && (
        <IconButton
          data-testid='create-risk'
          preset='PURPLE'
          onClick={onCreateRisk}
          disabled={!canCreateRisk}
          description='Create Form'>
          <AddIcon />
        </IconButton>
      )}
      <IconButton
        data-testid='edit-view'
        preset='PURPLE'
        onClick={showRiskViewDrawer}
        disabled={!canCreateRisk}
        description='Edit View'>
        <EditIcon />
      </IconButton>
      <IconButton preset='PURPLE' isActive={isFilterActive} onClick={showFilter} description='Filter'>
        <FilterIcon />
      </IconButton>
      {!isFormView && (
        <IconButton
          data-testid='delete-view'
          onClick={deleteRiskView}
          preset='RED_INVERTED'
          disabled={!manageRiskTemplates}
          description='Delete View'>
          <DeleteIcon color='inherit' />
        </IconButton>
      )}
    </ButtonsWrapper>
  )
}

export default RiskViewButtons
